import {
  mdiAccountQuestion,
  mdiAlertCircle,
  mdiClose,
  mdiCurrencyUsdOff,
  mdiFileCancel,
  mdiFileCheck,
  mdiFileClock,
  mdiFileEye,
  mdiFileMove,
  mdiNotEqualVariant,
} from '@mdi/js';
import Icon from '@mdi/react';
import React, { useCallback } from 'react';
import styled, { useTheme } from 'styled-components';
import { CancelButton } from 'common/components/cardStyles';
import { requestAcknowledgeNotifications } from 'common/state/notifications/thunkActions';
import {
  Notification,
  NotificationKeys,
  NotificationLevels,
  NotificationSatuses,
} from 'common/state/notifications/actions';
import { CURRENCY_CODES } from 'common/configs/currency';
import { Currency } from 'common/classes/currency';

const ScrollContainer = styled.div`
  width: 320px;
  max-height: 360px;
  padding-right: 0.5rem;
  overflow-y: scroll;
`;

const NotificationContainer = styled.figure`
  display: flex;
  flex-direction: column;
  padding: 1rem 0 0 0.5rem;
  margin: 0;

  &::after {
    content: ' ';
    margin-top: 1rem;
    flex: 1;
    border-bottom: solid 1px ${(props) => props.theme.cardLevel1};
  }

  &:last-child {
    &::after {
      content: ' ';
      border-bottom: none;
    }
  }

  &:first-child {
    padding-top: 0.75rem;
  }
`;

const NotificationHeader = styled.header`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CenterAlignedContainer = styled.div`
  display: flex;
  align-items: center;
`;

const GroupTitle = styled.div`
  font-weight: bold;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-left: 0.5rem;
  letter-spacing: 1px;
  border-bottom: 1px solid ${({ theme }) => theme.cardLevel1};
  width: calc(100% - 0.5rem);
  padding: 0.25rem 0;
  margin-top: 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const NotificationTitle = styled.strong`
  margin-left: 0.25rem;
  font-size: 0.875rem;
`;

const NotificationDate = styled.div`
  margin-right: 0.25rem;
  font-size: 0.75rem;
  color: ${(props) => props.theme.disabled};
`;

const NotificationCaption = styled.figcaption`
  margin-left: 0.25rem;
`;

const NoNotificationsContainer = styled.div`
  padding: 0.25rem 0.5rem;
  opacity: 0.7;
`;

export interface NotificationsDropdownProps {
  newNotifications: Notification[];
  ackedNotifications: Notification[];
  getNotifications: () => void;
}

export interface NotificationListItemProps {
  notification: Notification;
  ackNotification: (id: number) => void;
}

const LEVEL_COLOUR_MAP = {
  [NotificationLevels.INFO]: 'foreground',
  [NotificationLevels.WARNING]: 'warning',
  [NotificationLevels.DANGER]: 'danger',
};

const KEY_ICON_MAP = {
  [NotificationKeys.REQUESTED]: mdiAccountQuestion,
  [NotificationKeys.SENT]: mdiFileMove,
  [NotificationKeys.OPEN]: mdiFileEye,
  [NotificationKeys.EXPIRED]: mdiFileClock,
  [NotificationKeys.BAD_MOLLY_TOTAL]: mdiNotEqualVariant,
  [NotificationKeys.BAD_GAMBIT_TOTAL]: mdiNotEqualVariant,
  [NotificationKeys.CANCELED]: mdiFileCancel,
  [NotificationKeys.SUBMITTED]: mdiFileCheck,
  [NotificationKeys.LOW_BALANCE]: mdiCurrencyUsdOff,
  [NotificationKeys.UNACCEPTED]: mdiFileCancel,
  [NotificationKeys.ACCEPTED]: mdiFileCheck,
  default: mdiAlertCircle,
};

const currencyCodeRegex = Object.values(CURRENCY_CODES).join('|');
const currencyCodeFirstRegex = new RegExp(
  `(${currencyCodeRegex}) ([\\d\\-.]+)`,
  'g'
);
const currencyCodeSecondRegex = new RegExp(
  `([\\d\\-.]+) (${currencyCodeRegex})`,
  'g'
);
const NotificationListItem = ({
  notification,
  ackNotification,
}: NotificationListItemProps) => {
  const theme = useTheme();
  let parsedMessage = notification.message;

  Array.from(parsedMessage.matchAll(currencyCodeFirstRegex))?.forEach(
    ([match, ccy, amount]) =>
      (parsedMessage = parsedMessage.replaceAll(
        match,
        new Currency(amount, ccy).toString() + ` (${match})`
      ))
  );
  Array.from(parsedMessage.matchAll(currencyCodeSecondRegex))?.forEach(
    ([match, amount, ccy]) =>
      (parsedMessage = parsedMessage.replaceAll(
        match,
        new Currency(amount, ccy).toString()
      ))
  );

  return (
    <NotificationContainer key={notification.id}>
      <NotificationHeader>
        <CenterAlignedContainer>
          <Icon
            path={KEY_ICON_MAP[notification.key] || KEY_ICON_MAP.default}
            size={0.75}
            color={theme[LEVEL_COLOUR_MAP[notification.level]]}
          />
          <NotificationTitle>{notification.notifier}</NotificationTitle>
        </CenterAlignedContainer>
        <CenterAlignedContainer>
          <NotificationDate>
            {new Date(notification.date).toLocaleDateString()}
          </NotificationDate>
          {notification.status === NotificationSatuses.NEW && (
            <CancelButton onClick={() => ackNotification(notification.id)}>
              <Icon size={0.8} path={mdiClose} />
            </CancelButton>
          )}
        </CenterAlignedContainer>
      </NotificationHeader>
      <NotificationCaption>{parsedMessage}</NotificationCaption>
    </NotificationContainer>
  );
};

function NotificationsDropdown({
  newNotifications,
  ackedNotifications,
  getNotifications,
}: NotificationsDropdownProps) {
  const ackNotification = useCallback(
    (id: number) => {
      requestAcknowledgeNotifications(id).then(getNotifications);
    },
    [getNotifications]
  );

  return (
    <ScrollContainer>
      {!!newNotifications?.length && (
        <>
          <GroupTitle>New</GroupTitle>
          {newNotifications.map((notification) => (
            <NotificationListItem
              notification={notification}
              ackNotification={ackNotification}
              key={notification.id}
            />
          ))}
        </>
      )}
      {!!ackedNotifications?.length && (
        <>
          <GroupTitle>Seen</GroupTitle>
          {ackedNotifications.map((notification) => (
            <NotificationListItem
              notification={notification}
              ackNotification={ackNotification}
              key={notification.id}
            />
          ))}
        </>
      )}
      {!newNotifications?.length && !ackedNotifications.length && (
        <NoNotificationsContainer>
          There are no new notifications.
        </NoNotificationsContainer>
      )}
    </ScrollContainer>
  );
}

export default NotificationsDropdown;
